import React, { useRef, useCallback } from 'react';
import Nav from '../components/Nav';
import Hero from '../components/Hero';
import About from '../components/About';
import Projects from '../components/Projects';
import { projects } from '../data/portfolio';
import ProjectModal from '../components/ProjectModal';
import Experience from '../components/Experience';
import Stack from '../components/Stack';
import CommandInterface from '../components/CommandInterface';
import Contact from '../components/Contact';
import CustomCursor from '../components/CustomCursor';
import { useCommand } from '../hooks/useCommand';

const Portfolio = () => {
  // Refs for each section (used by command scroll-to)
  const sectionRefs = useRef({
    hero:       null,
    about:      null,
    projects:   null,
    experience: null,
    stack:      null,
    command:    null,
    contact:    null,
  });

  // Command input ref (for Cmd+K focus)
  const commandInputRef = useRef(null);

  // Register section ref helper
  const registerSection = useCallback((id) => (el) => {
    if (el) sectionRefs.current[id] = el;
  }, []);

  // Command system
  const {
    input,
    history,
    suggestions,
    suggestionIndex,
    activeFilter,
    openProjectId,
    handleInputChange,
    handleKeyDown,
    execute,
    acceptSuggestion,
    closeProject,
  } = useCommand({ sectionRefs });

  // Focus command input (called by Nav ⌘K and onCommandFocus prop)
  const focusCommand = useCallback(() => {
    const el = document.getElementById('command');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Delay focus slightly to let scroll finish
    setTimeout(() => commandInputRef.current?.focus(), 300);
  }, []);

  return (
    <>
      {/* Custom cursor — desktop only */}
      <CustomCursor />

      {/* Navigation */}
      <Nav onCommandFocus={focusCommand} />

      {/* Main content */}
      <main>
        {/* Hero */}
        <div ref={registerSection('hero')}>
          <Hero />
        </div>

        {/* About */}
        <div ref={registerSection('about')}>
          <About />
        </div>

        {/* Projects */}
        <div ref={registerSection('projects')}>
          <Projects
            activeFilter={activeFilter}
            onOpen={(id) => {
              // Find project by id and use its slug for the command
              const p = projects.find((proj) => proj.id === id);
              if (p) execute(`open ${p.slug}`);
            }}
          />
        </div>

        {/* Experience */}
        <div ref={registerSection('experience')}>
          <Experience />
        </div>

        {/* Tech Stack */}
        <div ref={registerSection('stack')}>
          <Stack />
        </div>

        {/* Command Interface */}
        <div ref={registerSection('command')}>
          <CommandInterface
            inputRef={commandInputRef}
            input={input}
            history={history}
            suggestions={suggestions}
            suggestionIndex={suggestionIndex}
            handleInputChange={handleInputChange}
            handleKeyDown={handleKeyDown}
            execute={execute}
            acceptSuggestion={acceptSuggestion}
          />
        </div>

        {/* Contact */}
        <div ref={registerSection('contact')}>
          <Contact />
        </div>
      </main>

      {/* Project Detail Modal */}
      {openProjectId && (
        <ProjectModal
          projectId={openProjectId}
          onClose={closeProject}
        />
      )}
    </>
  );
};

export default Portfolio;
